/**
 * IA ANALYTICS — Dashboard de Monitoramento do Assistente Gringão IA
 * ══════════════════════════════════════════════════════════════════════
 * Exibe métricas de uso, custo, feedback e alertas proativos.
 * Inclui aba de Auditoria I.A com histórico completo de interações.
 * Rota: /assistente-ia/analytics
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner, Table, ProgressBar, Modal, Button, Nav } from 'react-bootstrap';
import { fetchIAAnalytics, fetchIAAdminLogs } from '../../services/api';
import { FiRefreshCw, FiThumbsUp, FiThumbsDown, FiTrendingUp, FiDollarSign, FiMessageSquare, FiList } from 'react-icons/fi';

const fmtBRL = (val) =>
  val == null ? 'R$ 0,00'
  : Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4 });

const fmtNum = (val) => val == null ? '0' : Number(val).toLocaleString('pt-BR');

const GRAVIDADE_COLOR = { critico: 'danger', aviso: 'warning', info: 'info' };

// ── Helpers de formatação usados na tabela de auditoria ──────────────────────
const formatCustoBRL = (valor) => {
  if (!valor || valor === 0) return <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>—</span>;
  return <span style={{ color: '#059669', fontWeight: 600, fontSize: '0.75rem' }}>R$ {valor.toFixed(4)}</span>;
};

const MODEL_MAP = {
  'google/gemini-2.0-flash-001':         { label: 'Gemini Flash',  bg: '#dcfce7', color: '#166534', icon: '⚡' },
  'google/gemini-2.5-pro-preview':       { label: 'Gemini Pro',    bg: '#dbeafe', color: '#1d4ed8', icon: '🧠' },
  'google/gemini-2.5-pro-preview-05-06': { label: 'Gemini Ultra',  bg: '#ede9fe', color: '#6d28d9', icon: '🔮' },
  'anthropic/claude-sonnet-4-5':         { label: 'Claude Sonnet', bg: '#fee2e2', color: '#991b1b', icon: '✦' },
  'anthropic/claude-opus-4':             { label: 'Claude Opus',   bg: '#ffedd5', color: '#9a3412', icon: '✦✦' },
  'guardrail':                            { label: 'Guardrail',     bg: '#f3f4f6', color: '#6b7280', icon: '🛡️' },
};

const formatModelo = (modelo) => {
  if (!modelo) return <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>—</span>;
  const m = MODEL_MAP[modelo] || { label: modelo.split('/').pop(), bg: '#f3f4f6', color: '#374151', icon: '🤖' };
  return (
    <span title={modelo} style={{
      backgroundColor: m.bg, color: m.color,
      border: `1px solid ${m.color}40`,
      borderRadius: '4px', padding: '2px 6px',
      fontSize: '0.68rem', fontWeight: 600,
      whiteSpace: 'nowrap', cursor: 'help'
    }}>
      {m.icon} {m.label}
    </span>
  );
};

const formatTokens = (total, prompt, completion) => {
  if (!total || total === 0) return <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>—</span>;
  return (
    <span title={`Input: ${(prompt||0).toLocaleString('pt-BR')} | Output: ${(completion||0).toLocaleString('pt-BR')}`} style={{ cursor: 'help' }}>
      <Badge bg="secondary" style={{ fontSize: '0.68rem', fontWeight: 500 }}>
        {total.toLocaleString('pt-BR')}
      </Badge>
    </span>
  );
};

// ── Componente principal ─────────────────────────────────────────────────────
const IAAnalytics = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [iaLogs, setIaLogs]     = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');

  // Modal de detalhe do log
  const [showModal, setShowModal]     = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const carregar = async () => {
    setLoading(true);
    const res = await fetchIAAnalytics();
    if (res && res.success) setData(res);
    setLoading(false);
  };

  const carregarLogs = async () => {
    setLogsLoading(true);
    const res = await fetchIAAdminLogs();
    if (res && res.success) setIaLogs(res.logs || []);
    setLogsLoading(false);
  };

  useEffect(() => {
    carregar();
    carregarLogs();
  }, []);

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
      <Spinner animation="border" variant="primary" />
    </div>
  );

  if (!data) return (
    <div className="alert alert-danger m-4">Erro ao carregar analytics da IA.</div>
  );

  const custo                  = data.custo                  || {};
  const top_perguntas          = Array.isArray(data.top_perguntas)          ? data.top_perguntas          : [];
  const distribuicao_dominios  = Array.isArray(data.distribuicao_dominios)  ? data.distribuicao_dominios  : [];
  const memoria                = data.memoria                || {};
  const feedback               = data.feedback               || {};
  const modelos                = Array.isArray(data.modelos)                ? data.modelos                : [];
  const alertas_proativos      = Array.isArray(data.alertas_proativos)      ? data.alertas_proativos      : [];
  const custo_por_dia          = Array.isArray(data.custo_por_dia)          ? data.custo_por_dia          : [];

  const totalPerguntas = custo.total_perguntas || distribuicao_dominios.reduce((s, d) => s + (d.total || 0), 0) || 0;
  const positivosPct   = feedback.total > 0
    ? Math.round((feedback.positivos / feedback.total) * 100) : 0;

  const totalCustoBRL = custo.total_brl > 0
    ? custo.total_brl
    : iaLogs.reduce((acc, l) => acc + (l.custo_brl || 0), 0);
  const totalTokens   = iaLogs.reduce((acc, l) => acc + (l.tokens_total || 0), 0);
  const totalUSD      = custo.total_usd > 0
    ? custo.total_usd
    : totalCustoBRL / 5.8;

  return (
    <div className="p-4" style={{ background: '#f8f9fa', minHeight: '100vh', color: '#212529' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0 fw-bold text-dark">
            📊 IA Analytics — Assistente Gringão
          </h4>
          <small className="text-muted">Últimos {data.periodo_dias} dias</small>
        </div>
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
          onClick={() => { carregar(); carregarLogs(); }}
        >
          <FiRefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* Tabs de navegação */}
      <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={setActiveTab}>
        <Nav.Item>
          <Nav.Link eventKey="analytics" className="d-flex align-items-center gap-2">
            <FiTrendingUp size={14} /> Métricas & Custos
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="auditoria" className="d-flex align-items-center gap-2">
            <FiList size={14} /> Auditoria I.A
            {iaLogs.length > 0 && <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>{iaLogs.length}</Badge>}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* ── ABA MÉTRICAS ─────────────────────────────────────────────────── */}
      {activeTab === 'analytics' && (
        <>

          {/* KPI Cards */}
          <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="shadow-sm h-100 border">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <FiMessageSquare className="text-primary" />
                    <small className="text-muted">Total de Perguntas</small>
                  </div>
                  <div className="fs-4 fw-bold text-dark">{fmtNum(totalPerguntas)}</div>
                  <small className="text-muted">no período</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm h-100 border">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <FiDollarSign className="text-success" />
                    <small className="text-muted">Custo Total</small>
                  </div>
                  <div className="fs-4 fw-bold text-dark">{fmtBRL(totalCustoBRL)}</div>
                  <small className="text-muted">≈ USD {Number(totalUSD || 0).toFixed(4)}</small>
                  {totalTokens > 0 && (
                    <div style={{ marginTop: '4px' }}>
                      <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                        🪙 {totalTokens.toLocaleString('pt-BR')} tokens
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm h-100 border">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <FiThumbsUp className="text-info" />
                    <small className="text-muted">Satisfação</small>
                  </div>
                  <div className="fs-4 fw-bold text-dark">{positivosPct}%</div>
                  <ProgressBar
                    now={positivosPct}
                    variant={positivosPct >= 70 ? 'success' : positivosPct >= 40 ? 'warning' : 'danger'}
                    style={{ height: '4px', marginTop: '6px' }}
                  />
                  <small className="text-muted">{fmtNum(feedback.total)} feedbacks</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm h-100 border">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <FiTrendingUp className="text-warning" />
                    <small className="text-muted">Memória (Queries Validadas)</small>
                  </div>
                  <div className="fs-4 fw-bold text-dark">{fmtNum(memoria.validadas)}</div>
                  <small className="text-muted">{fmtNum(memoria.quarentena)} em quarentena</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            {/* Top Perguntas */}
            <Col md={7}>
              <Card className="shadow-sm h-100 border">
                <Card.Header className="bg-white border-bottom fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                  Top Perguntas
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    <Table size="sm" hover className="mb-0" style={{ fontSize: '0.8rem' }}>
                      <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                        <tr>
                          <th>Pergunta</th>
                          <th className="text-end" style={{ width: 60 }}>Vezes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {top_perguntas.map((p, i) => (
                          <tr key={i}>
                            <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={p.pergunta}>
                              {p.pergunta}
                            </td>
                            <td className="text-end">
                              <Badge bg="secondary">{p.total}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Distribuição por Domínio */}
            <Col md={5}>
              <Card className="shadow-sm h-100 border">
                <Card.Header className="bg-white border-bottom fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                  Distribuição por Domínio
                </Card.Header>
                <Card.Body>
                  {distribuicao_dominios.map((d, i) => {
                    const pct = totalPerguntas > 0 ? Math.round((d.total / totalPerguntas) * 100) : 0;
                    return (
                      <div key={i} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-secondary">{d.agente_ativado || 'geral'}</small>
                          <small className="text-muted">{d.total} ({pct}%)</small>
                        </div>
                        <ProgressBar now={pct} variant="info" style={{ height: '6px' }} />
                      </div>
                    );
                  })}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            {/* Custo por Dia */}
            <Col md={7}>
              <Card className="shadow-sm border">
                <Card.Header className="bg-white border-bottom fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                  Custo Diário (BRL)
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                    <Table size="sm" hover className="mb-0" style={{ fontSize: '0.78rem' }}>
                      <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                        <tr>
                          <th>Data</th>
                          <th className="text-end">Perguntas</th>
                          <th className="text-end">Custo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {custo_por_dia.slice(0, 30).map((d, i) => (
                          <tr key={i}>
                            <td>{d.dia}</td>
                            <td className="text-end">{fmtNum(d.total_perguntas)}</td>
                            <td className="text-end text-success fw-semibold">{fmtBRL(d.custo_brl)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Modelos usados + Feedback */}
            <Col md={5}>
              <Card className="shadow-sm border mb-3">
                <Card.Header className="bg-white border-bottom fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                  Modelos Utilizados
                </Card.Header>
                <Card.Body>
                  {modelos.map((m, i) => (
                    <div key={i} className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-secondary" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.modelo_usado}
                      </small>
                      <Badge bg="secondary">{fmtNum(m.total)}</Badge>
                    </div>
                  ))}
                </Card.Body>
              </Card>

              <Card className="shadow-sm border">
                <Card.Header className="bg-white border-bottom fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                  Feedback dos Usuários
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-around text-center">
                    <div>
                      <FiThumbsUp size={20} className="text-success mb-1" />
                      <div className="fs-5 fw-bold text-dark">{fmtNum(feedback.positivos)}</div>
                      <small className="text-muted">Positivos</small>
                    </div>
                    <div>
                      <FiThumbsDown size={20} className="text-danger mb-1" />
                      <div className="fs-5 fw-bold text-dark">{fmtNum(feedback.negativos)}</div>
                      <small className="text-muted">Negativos</small>
                    </div>
                    <div>
                      <FiMessageSquare size={20} className="text-info mb-1" />
                      <div className="fs-5 fw-bold text-dark">{fmtNum(feedback.total)}</div>
                      <small className="text-muted">Total</small>
                    </div>
                  </div>
                  {feedback.total > 0 && (
                    <div className="mt-3">
                      <ProgressBar>
                        <ProgressBar variant="success" now={Math.round((feedback.positivos / feedback.total) * 100)} key={1} />
                        <ProgressBar variant="danger"  now={Math.round((feedback.negativos / feedback.total) * 100)} key={2} />
                      </ProgressBar>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* ── ABA AUDITORIA I.A ─────────────────────────────────────────────── */}
      {activeTab === 'auditoria' && (
        <>
          {logsLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Card className="shadow-sm border-0" style={{ background: '#0f172a' }}>
              <Card.Header style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}
                className="d-flex justify-content-between align-items-center">
                <span className="fw-bold text-light">
                  <Badge bg="danger" className="me-2">IA Tracker</Badge>
                  Histórico Sistêmico de Perguntas
                </span>
                <div className="d-flex gap-3 align-items-center">
                  <small style={{ color: '#6b7280' }}>
                    🪙 <span style={{ color: '#34d399', fontWeight: 600 }}>R$ {totalCustoBRL.toFixed(4)}</span> total
                    &nbsp;•&nbsp;
                    <span style={{ color: '#94a3b8' }}>{totalTokens.toLocaleString('pt-BR')} tokens</span>
                  </small>
                  <Badge bg="secondary">{iaLogs.length} Registros</Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'hidden' }}>
                  <Table hover variant="dark" size="sm" className="mb-0 align-middle"
                    style={{ fontSize: '0.85rem', tableLayout: 'fixed', width: '100%' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#1e293b' }}>
                      <tr>
                        <th className="py-2 px-3" style={{ color: '#94a3b8', whiteSpace: 'nowrap', width: '130px' }}>Data</th>
                        <th className="py-2" style={{ color: '#94a3b8', width: '90px' }}>Loja</th>
                        <th className="py-2" style={{ color: '#94a3b8', width: '110px' }}>Usuário</th>
                        <th className="py-2" style={{ color: '#94a3b8' }}>Pergunta</th>
                        <th className="py-2" style={{ color: '#94a3b8' }}>Resposta</th>
                        <th className="py-2 text-center" style={{ color: '#94a3b8', width: '110px', whiteSpace: 'nowrap' }}>LLM</th>
                        <th className="py-2 text-center" style={{ color: '#94a3b8', width: '80px', whiteSpace: 'nowrap' }}>Tokens</th>
                        <th className="py-2 text-center" style={{ color: '#94a3b8', width: '90px', whiteSpace: 'nowrap' }}>Custo R$</th>
                      </tr>
                    </thead>
                    <tbody>
                      {iaLogs.length > 0 ? iaLogs.map(l => (
                        <tr key={l.id} onClick={() => { setSelectedLog(l); setShowModal(true); }}
                          style={{ cursor: 'pointer' }} title="Clique para ver detalhes completos">
                          <td className="px-3" style={{ fontSize: '0.75rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.ts}
                          </td>
                          <td>
                            <Badge bg={l.filtro_loja === 'todas' ? 'primary' : 'warning'}
                              text={l.filtro_loja !== 'todas' ? 'dark' : 'light'}
                              className="text-uppercase">{l.filtro_loja}
                            </Badge>
                          </td>
                          <td><strong style={{ color: '#e2e8f0' }}>{l.nome_usuario}</strong></td>
                          <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#38bdf8' }} title={l.pergunta}>
                            {l.pergunta}
                          </td>
                          <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#9ca3af' }} title={l.resposta}>
                            {l.resposta}
                          </td>
                          <td className="text-center">{formatModelo(l.modelo_usado)}</td>
                          <td className="text-center">{formatTokens(l.tokens_total, l.tokens_prompt, l.tokens_completion)}</td>
                          <td className="text-center">{formatCustoBRL(l.custo_brl)}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="8" className="text-center py-5 text-muted">
                            Ainda não houve tráfego com a Inteligência Artificial no Tracker de Auditoria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {iaLogs.length > 0 && (
                      <tfoot>
                        <tr style={{ borderTop: '1px solid #334155', backgroundColor: '#1a1d21' }}>
                          <td colSpan="6" className="px-3 py-2" style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                            Total acumulado ({iaLogs.length} interações)
                          </td>
                          <td className="text-center py-2">
                            <Badge bg="dark" style={{ border: '1px solid #374151', color: '#94a3b8', fontSize: '0.7rem' }}>
                              {totalTokens.toLocaleString('pt-BR')}
                            </Badge>
                          </td>
                          <td className="text-center py-2">
                            <span style={{ color: '#34d399', fontWeight: 700, fontSize: '0.8rem' }}>
                              R$ {totalCustoBRL.toFixed(4)}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* ── Modal de detalhes do log ─────────────────────────────────────── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#1a1d21', borderBottom: '1px solid #333' }}>
          <Modal.Title style={{ color: '#e2e8f0', fontSize: '1rem' }}>
            <span className="me-2">🧠</span> Detalhe da Interação com a I.A
            {selectedLog && (
              <Badge bg={selectedLog.filtro_loja === 'todas' ? 'primary' : 'warning'}
                text={selectedLog.filtro_loja !== 'todas' ? 'dark' : 'light'}
                className="ms-3 text-uppercase">{selectedLog.filtro_loja}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#0d1117', color: '#c9d1d9', maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedLog && (
            <div>
              {/* Metadados */}
              <div className="d-flex gap-4 mb-3 pb-3" style={{ borderBottom: '1px solid #21262d', fontSize: '0.85rem', flexWrap: 'wrap' }}>
                <div><span style={{ color: '#8b949e' }}>Usuário:</span> <strong style={{ color: '#58a6ff' }}>{selectedLog.nome_usuario}</strong></div>
                <div><span style={{ color: '#8b949e' }}>Data:</span> <strong style={{ color: '#e2e8f0' }}>{selectedLog.ts}</strong></div>
                {selectedLog.modelo_usado && (
                  <div><span style={{ color: '#8b949e' }}>LLM:</span> <span className="ms-1">{formatModelo(selectedLog.modelo_usado)}</span></div>
                )}
                {selectedLog.tokens_total > 0 && (
                  <>
                    <div><span style={{ color: '#8b949e' }}>Tokens Input:</span> <strong style={{ color: '#f6c90e' }}>{(selectedLog.tokens_prompt || 0).toLocaleString('pt-BR')}</strong></div>
                    <div><span style={{ color: '#8b949e' }}>Tokens Output:</span> <strong style={{ color: '#f6c90e' }}>{(selectedLog.tokens_completion || 0).toLocaleString('pt-BR')}</strong></div>
                    <div><span style={{ color: '#8b949e' }}>Total Tokens:</span> <strong style={{ color: '#f6c90e' }}>{(selectedLog.tokens_total || 0).toLocaleString('pt-BR')}</strong></div>
                    <div><span style={{ color: '#8b949e' }}>Custo:</span> <strong style={{ color: '#34d399' }}>R$ {(selectedLog.custo_brl || 0).toFixed(5)}</strong></div>
                  </>
                )}
              </div>

              {/* Pergunta */}
              <div className="mb-4">
                <h6 style={{ color: '#58a6ff', fontWeight: 600, marginBottom: '0.75rem' }}>
                  <span className="me-2">💬</span> Pergunta do Usuário
                </h6>
                <div style={{
                  backgroundColor: '#161b22', border: '1px solid #30363d',
                  borderRadius: '8px', padding: '1rem 1.25rem',
                  color: '#e2e8f0', fontSize: '0.95rem', lineHeight: '1.6',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                }}>
                  {selectedLog.pergunta || 'Sem pergunta registrada'}
                </div>
              </div>

              {/* Resposta */}
              <div>
                <h6 style={{ color: '#3fb950', fontWeight: 600, marginBottom: '0.75rem' }}>
                  <span className="me-2">🤖</span> Resposta da I.A
                </h6>
                <div style={{
                  backgroundColor: '#161b22', border: '1px solid #30363d',
                  borderRadius: '8px', padding: '1rem 1.25rem',
                  color: '#c9d1d9', fontSize: '0.9rem', lineHeight: '1.7',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                }}>
                  {selectedLog.resposta || 'Sem resposta registrada'}
                </div>
              </div>

              {/* Erro (se houver) */}
              {selectedLog.erros && (
                <div className="mt-4">
                  <h6 style={{ color: '#f85149', fontWeight: 600, marginBottom: '0.75rem' }}>
                    <span className="me-2">⚠️</span> Erro Registrado
                  </h6>
                  <div style={{
                    backgroundColor: '#1c1210', border: '1px solid #5a2d22',
                    borderRadius: '8px', padding: '1rem 1.25rem',
                    color: '#f85149', fontSize: '0.85rem', whiteSpace: 'pre-wrap'
                  }}>
                    {selectedLog.erros}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1a1d21', borderTop: '1px solid #333' }}>
          <Button variant="outline-light" size="sm" onClick={() => setShowModal(false)}>Fechar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default IAAnalytics;
